import { useMutation, useQuery } from '@tanstack/react-query'
import { PencilLine, ShieldCheck, Trash2 } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import type {
  ExchangeDetail,
  ExchangeReasonCode,
  ProductSummary,
} from '@aeris/contracts'
import {
  MONEY_SCALE,
  QUANTITY_SCALE,
  UNIT_PRICE_SCALE,
  add,
  compare,
  formatMoney,
  fromString,
  lineTotal,
  parseHumanAmount,
  sum,
  toString as decimalToString,
  zero,
  type Decimal,
} from '@aeris/money'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  RadioGroup,
  SearchInput,
  Spinner,
  Textarea,
  cn,
} from '@aeris/ui'

import { createExchange } from '../lib/api/exchanges.ts'
import { ApiError, fetchProducts } from '../lib/api.ts'
import { useDebounced } from '../lib/use-debounced.ts'

import { AuthorisationDialog } from './authorisation-dialog.tsx'
import {
  EXCHANGE_KIND_OPTIONS,
  KIND_CARDS_CLASS,
} from './exchange-kind-options.ts'
import { ExchangeReasonPicker } from './exchange-reason-picker.tsx'
import { PartyPicker } from './party-picker.tsx'

/**
 * Troca ou vale de produto de fora, com a senha do admin (F148).
 *
 * A loja assumida de outro dono: o cliente comprou lá, antes do Aeris, e não
 * há venda para achar. O trocador monta o que voltou. A peça pode vir do
 * cadastro, com o preço de tabela, ou ser DIGITADA — nome e valor —, porque
 * ninguém sabe o código nem o preço do sistema antigo. O admin confere e pode
 * mudar cada valor na tela da senha. O que ele aprova vai gravado junto com a
 * senha, e o servidor recusa um pedido diferente disso.
 *
 * A senha vale para ESTA troca (o id é gerado aqui) e para ESTE combinado:
 * mudar cliente, tipo ou peça depois dela pede a senha de novo.
 */

type Piece = {
  /** A chave da linha na tela: o produto, ou um id da peça digitada. */
  key: string
  /** Nulo na peça digitada, que não está no cadastro. */
  variantId: string | null
  name: string
  /** O preço de tabela, como texto de máquina. Nulo quando não há. */
  tablePrice: string | null
  quantity: string
  /** Só na digitada: o valor por unidade que o trocador informou. */
  typedValue: string
}

const ONE = fromString('1', QUANTITY_SCALE)

const quantityText = (value: Decimal): string =>
  decimalToString(value)
    .replace(/\.?0+$/, '')
    .replace('.', ',')

/** "40,00" do campo, ou o preço de tabela, em reais. */
const moneyText = (wire: string | null): string =>
  wire === null
    ? ''
    : decimalToString(fromString(wire, UNIT_PRICE_SCALE))
        .replace(/(\.\d{2})\d*$/, '$1')
        .replace('.', ',')

const parseQuantity = (text: string): Decimal | null =>
  text.trim() === '' ? null : parseHumanAmount(text, QUANTITY_SCALE)

const parseValue = (text: string): Decimal | null =>
  text.trim() === '' ? null : parseHumanAmount(text, MONEY_SCALE)

/** O nome como o servidor compara: sem espaço sobrando e sem caixa. */
const typedKey = (name: string): string =>
  name.trim().replace(/\s+/g, ' ').toLocaleLowerCase('pt-BR')

const variantOf = (product: ProductSummary) =>
  product.variants.find((entry) => entry.isDefault) ?? product.variants[0]

/** A busca de produto, com a lista de resultados (a mesma do "O que leva"). */
const ProductSearch = ({
  label,
  onPick,
}: {
  label: string
  onPick: (product: ProductSummary) => void
}): ReactNode => {
  const listId = useId()
  const [text, setText] = useState('')
  const term = useDebounced(text.trim())
  const products = useQuery({
    queryKey: ['outside-exchange-product', term],
    queryFn: async ({ signal }) =>
      await fetchProducts(
        {
          search: term,
          status: 'active',
          page: 1,
          pageSize: 8,
          include: ['price'],
        },
        signal
      ),
    enabled: term.length > 1,
  })
  const results = products.data?.items ?? []

  return (
    <div className="flex flex-col gap-2">
      <Field label={label} hint="Bipe o código de barras ou busque pelo nome.">
        {(field) => (
          <SearchInput
            {...field}
            wrapperClassName="min-w-0"
            value={text}
            placeholder="Nome, código ou código de barras"
            aria-controls={listId}
            onChange={(event) => {
              setText(event.target.value)
            }}
          />
        )}
      </Field>
      {term.length < 2 ? null : products.isPending ? (
        <p className="flex items-center gap-2 text-(length:--text-sm) text-(--text-muted)">
          <Spinner /> Buscando produtos…
        </p>
      ) : results.length === 0 ? (
        <p className="text-(length:--text-sm) text-(--text-muted)">
          {`Nenhum produto ativo com “${term}”.`}
        </p>
      ) : (
        <ul
          id={listId}
          aria-label={`${label}: resultados`}
          className="flex flex-col gap-1"
        >
          {results.map((product) => (
            <li key={product.id}>
              <button
                type="button"
                onClick={() => {
                  onPick(product)
                  setText('')
                }}
                className="flex w-full items-center justify-between gap-3 rounded-(--radius-md) border border-(--border-default) px-3 py-1.5 text-left hover:bg-(--surface-sunken)"
              >
                <span className="truncate">{product.name}</span>
                <span className="shrink-0 font-(--font-mono) text-(--text-secondary)">
                  {product.price?.basePrice == null
                    ? 'sem preço'
                    : formatMoney(
                        fromString(product.price.basePrice, UNIT_PRICE_SCALE)
                      )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const addPiece = (current: Piece[], product: ProductSummary): Piece[] => {
  const variant = variantOf(product)
  if (variant === undefined) return current
  const existing = current.find((piece) => piece.variantId === variant.id)
  if (existing !== undefined) {
    // O mesmo produto de novo é "mais um", como o bipe do PDV.
    const quantity = parseQuantity(existing.quantity) ?? zero(QUANTITY_SCALE)
    return current.map((piece) =>
      piece.variantId === variant.id
        ? { ...piece, quantity: quantityText(add(quantity, ONE)) }
        : piece
    )
  }
  return [
    ...current,
    {
      key: variant.id,
      variantId: variant.id,
      name: product.name,
      tablePrice: product.price?.basePrice ?? null,
      quantity: '1',
      typedValue: '',
    },
  ]
}

/** Uma peça que não está no cadastro: o trocador escreve nome e valor. */
const addTypedPiece = (current: Piece[]): Piece[] => [
  ...current,
  {
    key: `digitada-${crypto.randomUUID()}`,
    variantId: null,
    name: '',
    tablePrice: null,
    quantity: '1',
    typedValue: '',
  },
]

const PieceList = ({
  label,
  pieces,
  onChange,
}: {
  label: string
  pieces: Piece[]
  onChange: (next: Piece[]) => void
}): ReactNode => {
  const update = (key: string, patch: Partial<Piece>): void => {
    onChange(
      pieces.map((entry) =>
        entry.key === key ? { ...entry, ...patch } : entry
      )
    )
  }

  return pieces.length === 0 ? (
    <p className="rounded-(--radius-md) border border-dashed border-(--border-default) px-3 py-3 text-center text-(length:--text-sm) text-(--text-muted)">
      Nada escolhido ainda.
    </p>
  ) : (
    <ul aria-label={label} className="flex flex-col gap-2">
      {pieces.map((piece, index) => {
        const typed = piece.variantId === null
        const shown = piece.name.trim() === '' ? 'peça digitada' : piece.name
        return (
          <li
            key={piece.key}
            className="flex flex-wrap items-center justify-between gap-3 rounded-(--radius-md) border border-(--border-default) p-3"
          >
            {typed ? (
              <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <Input
                  className="min-w-0 flex-1"
                  aria-label={`Nome da peça ${String(index + 1)}`}
                  placeholder="Nome da peça, ex.: Capinha iPhone 11"
                  maxLength={200}
                  value={piece.name}
                  onChange={(event) => {
                    update(piece.key, { name: event.target.value })
                  }}
                />
                <Input
                  className="w-28 text-right font-(--font-mono)"
                  inputMode="decimal"
                  aria-label={`Valor da peça ${String(index + 1)}`}
                  placeholder="Valor"
                  value={piece.typedValue}
                  onChange={(event) => {
                    update(piece.key, { typedValue: event.target.value })
                  }}
                />
              </span>
            ) : (
              <span className="flex min-w-0 flex-col">
                <span className="font-(--weight-medium)">{piece.name}</span>
                <span className="text-(length:--text-xs) text-(--text-muted)">
                  {piece.tablePrice === null
                    ? 'sem preço na tabela: o admin informa o valor'
                    : `tabela ${formatMoney(fromString(piece.tablePrice, UNIT_PRICE_SCALE))} cada`}
                </span>
              </span>
            )}
            <span className="flex items-center gap-2">
              <Input
                className="w-20 text-center font-(--font-mono)"
                inputMode="decimal"
                aria-label={`Quantidade de ${shown}`}
                value={piece.quantity}
                onChange={(event) => {
                  update(piece.key, { quantity: event.target.value })
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Tirar ${shown}`}
                leadingIcon={
                  <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                }
                onClick={() => {
                  onChange(pieces.filter((entry) => entry.key !== piece.key))
                }}
              />
            </span>
          </li>
        )
      })}
    </ul>
  )
}

export const OutsideExchangeDialog = ({
  onDone,
  onClose,
}: {
  onDone: (exchange: ExchangeDetail) => void
  onClose: () => void
}): ReactNode => {
  // O id da troca, que a senha autoriza. Uma senha não serve para duas trocas.
  const [exchangeId] = useState(() => crypto.randomUUID())
  const [customerPartyId, setCustomerPartyId] = useState('')
  const [kind, setKind] = useState<'exchange' | 'voucher'>('voucher')
  const [returned, setReturned] = useState<Piece[]>([])
  const [outgoing, setOutgoing] = useState<Piece[]>([])
  /** Por que a peça voltou (F158). Sem pedido do caixa para herdar: aqui não há venda. */
  const [reasonCode, setReasonCode] = useState<ExchangeReasonCode | null>(null)
  const [note, setNote] = useState('')
  /**
   * O valor por unidade que o admin decide, por peça. Começa na tabela, ou no
   * valor que o trocador digitou. Mudar as peças zera o que o admin mexeu: ele
   * confere de novo na próxima senha.
   */
  const [values, setValues] = useState<Record<string, string>>({})
  const [asking, setAsking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const changeReturned = (next: Piece[]): void => {
    setReturned(next)
    setValues({})
  }

  const initialValue = (piece: Piece): string =>
    piece.variantId === null ? piece.typedValue : moneyText(piece.tablePrice)

  const returnedChecks = returned.map((piece) => ({
    piece,
    quantity: parseQuantity(piece.quantity),
    value: parseValue(values[piece.key] ?? initialValue(piece)),
  }))
  const outgoingChecks = outgoing.map((piece) => ({
    piece,
    quantity: parseQuantity(piece.quantity),
  }))

  const returnedQuantity = sum(
    returnedChecks.flatMap(({ quantity }) =>
      quantity === null ? [] : [quantity]
    ),
    QUANTITY_SCALE
  )
  const outgoingQuantity = sum(
    outgoingChecks.flatMap(({ quantity }) =>
      quantity === null ? [] : [quantity]
    ),
    QUANTITY_SCALE
  )

  const typedNames = returned
    .filter((piece) => piece.variantId === null)
    .map((piece) => typedKey(piece.name))

  const blocker = ((): string | null => {
    if (customerPartyId === '') return 'Escolha o cliente cadastrado.'
    if (reasonCode === null) return 'Diga por que a peça voltou.'
    if (returned.length === 0) return 'Adicione o que o cliente trouxe.'
    if (typedNames.some((name) => name === '')) {
      return 'Digite o nome da peça que o cliente trouxe.'
    }
    if (new Set(typedNames).size !== typedNames.length) {
      return 'A mesma peça digitada aparece duas vezes: junte numa linha só, com a quantidade.'
    }
    if (
      [...returnedChecks, ...(kind === 'exchange' ? outgoingChecks : [])].some(
        ({ quantity }) => quantity === null || quantity.units <= 0n
      )
    ) {
      return 'Tem produto sem quantidade.'
    }
    if (kind === 'exchange') {
      if (outgoing.length === 0) return 'Adicione o que o cliente leva.'
      if (outgoing.some((piece) => piece.tablePrice === null)) {
        return 'Tem produto de saída sem preço: cadastre o preço antes.'
      }
      if (compare(outgoingQuantity, returnedQuantity) > 0) {
        return 'Na troca o cliente leva no máximo a quantidade que trouxe. Mais que isso é venda, no caixa.'
      }
    }
    return null
  })()

  const valuesMissing = returnedChecks.some(
    ({ value }) => value === null || value.units <= 0n
  )
  const approvedTotal = sum(
    returnedChecks.flatMap(({ quantity, value }) =>
      quantity === null || value === null ? [] : [lineTotal(quantity, value)]
    ),
    MONEY_SCALE
  )

  /** Cada peça pelo produto, ou pelo nome digitado — nunca os dois. */
  const approvedLines = returnedChecks.flatMap(({ piece, quantity, value }) =>
    quantity === null || value === null
      ? []
      : [
          {
            ...(piece.variantId === null
              ? { description: piece.name.trim().replace(/\s+/g, ' ') }
              : { variantId: piece.variantId }),
            quantity: decimalToString(quantity),
            unitValue: decimalToString(value),
          },
        ]
  )

  const create = useMutation({
    mutationFn: async (authorisationId: string) =>
      await createExchange({
        id: exchangeId,
        kind,
        customerPartyId,
        authorisationId,
        returned: approvedLines.map((line) => ({
          ...line,
          // Sempre defeito (F145).
          restock: false,
          note: null,
        })),
        outgoing:
          kind === 'voucher'
            ? []
            : outgoingChecks.flatMap(({ piece, quantity }) =>
                quantity === null || piece.variantId === null
                  ? []
                  : [
                      {
                        variantId: piece.variantId,
                        quantity: decimalToString(quantity),
                      },
                    ]
              ),
        // Não-nulo: o `blocker` acima não deixa a senha do admin ser pedida sem ele.
        reasonCode: reasonCode ?? 'outro',
        note: note.trim() === '' ? null : note.trim(),
      }),
    onSuccess: (response) => {
      onDone(response.exchange)
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível concluir a troca.'
      )
    },
  })

  return (
    <>
      <Dialog open={!asking} onOpenChange={onClose}>
        <DialogContent
          title="Troca ou vale de produto de fora"
          description="Para o cliente que comprou antes do Aeris, na loja anterior. Precisa da senha de um admin, gerente ou dono."
          className="max-w-3xl"
        >
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault()
              if (blocker === null) {
                setError(null)
                setAsking(true)
              }
            }}
          >
            <Field label="Cliente">
              {(field) => (
                <PartyPicker
                  {...field}
                  value={customerPartyId}
                  onChange={setCustomerPartyId}
                  role="customer"
                />
              )}
            </Field>

            <Field label="O cliente quer">
              {(field) => (
                <RadioGroup
                  id={field.id}
                  name="outside-exchange-kind"
                  orientation="horizontal"
                  value={kind}
                  onValueChange={setKind}
                  options={EXCHANGE_KIND_OPTIONS}
                  className={KIND_CARDS_CLASS}
                />
              )}
            </Field>

            <Field label="Por que voltou">
              {() => (
                <ExchangeReasonPicker
                  value={reasonCode}
                  onValueChange={setReasonCode}
                />
              )}
            </Field>

            <section className="flex flex-col gap-2">
              <ProductSearch
                label="O que o cliente trouxe"
                onPick={(product) => {
                  changeReturned(addPiece(returned, product))
                }}
              />
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  leadingIcon={
                    <PencilLine aria-hidden="true" className="h-3.5 w-3.5" />
                  }
                  onClick={() => {
                    changeReturned(addTypedPiece(returned))
                  }}
                >
                  Digitar peça fora do cadastro
                </Button>
              </div>
              <PieceList
                label="O que voltou"
                pieces={returned}
                onChange={changeReturned}
              />
              <p className="text-(length:--text-xs) text-(--warning)">
                Com defeito, não volta ao estoque. Do cadastro, o valor vem da
                tabela; digitada, você informa nome e valor. O admin confirma na
                senha.
              </p>
            </section>

            {kind === 'exchange' ? (
              <section className="flex flex-col gap-2">
                <ProductSearch
                  label="O que o cliente leva"
                  onPick={(product) => {
                    setOutgoing((current) => addPiece(current, product))
                  }}
                />
                <PieceList
                  label="O que o cliente leva"
                  pieces={outgoing}
                  onChange={setOutgoing}
                />
              </section>
            ) : null}

            <Field label="Observação" hint="Sai no comprovante.">
              {(field) => (
                <Textarea
                  {...field}
                  rows={2}
                  value={note}
                  maxLength={500}
                  onChange={(event) => {
                    setNote(event.target.value)
                  }}
                />
              )}
            </Field>

            {error === null ? null : (
              <InlineAlert tone="danger">{error}</InlineAlert>
            )}

            <DialogFooter className="items-center justify-between">
              <span className="text-(length:--text-sm) text-(--text-muted)">
                {blocker ?? 'Pronto para a senha do admin.'}
              </span>
              <div className="flex gap-2">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  aria-disabled={blocker !== null}
                  className={blocker === null ? undefined : 'opacity-60'}
                  loading={create.isPending}
                  leadingIcon={
                    <ShieldCheck aria-hidden="true" className="h-4 w-4" />
                  }
                >
                  Pedir senha do admin
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {asking ? (
        <AuthorisationDialog
          title="Aprovar troca de produto de fora"
          description="Confira as peças e o valor de cada uma. O que for aprovado aqui é o que vira vale ou troca."
          action="exchange.without_sale"
          targetType="exchange"
          targetId={exchangeId}
          detail={{
            customerPartyId,
            kind,
            returned: approvedLines,
          }}
          canAuthorise={!valuesMissing}
          summary={
            <section
              aria-label="O que está sendo aprovado"
              className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-default) p-3"
            >
              <ul className="flex flex-col gap-2">
                {returnedChecks.map(({ piece, quantity, value }) => (
                  <li
                    key={piece.key}
                    className="flex flex-wrap items-center justify-between gap-2 text-(length:--text-sm)"
                  >
                    <span className="min-w-0">
                      {`${quantity === null ? '?' : quantityText(quantity)} × ${piece.name}`}
                      {piece.variantId === null ? (
                        <span className="text-(--text-muted)">
                          {' '}
                          (digitada, fora do cadastro)
                        </span>
                      ) : null}
                    </span>
                    <Input
                      className={cn(
                        'w-28 text-right font-(--font-mono)',
                        value === null || value.units <= 0n
                          ? 'border-(--danger)'
                          : undefined
                      )}
                      inputMode="decimal"
                      aria-label={`Valor por unidade de ${piece.name}`}
                      value={values[piece.key] ?? initialValue(piece)}
                      onChange={(event) => {
                        setValues((current) => ({
                          ...current,
                          [piece.key]: event.target.value,
                        }))
                      }}
                    />
                  </li>
                ))}
              </ul>
              <p className="text-right text-(length:--text-sm) font-(--weight-semibold)">
                {kind === 'voucher'
                  ? `Vale de ${formatMoney(approvedTotal)}`
                  : `Vale ${formatMoney(approvedTotal)} na troca`}
              </p>
              {valuesMissing ? (
                <p
                  role="alert"
                  className="text-(length:--text-xs) text-(--danger)"
                >
                  Toda peça precisa de um valor maior que zero.
                </p>
              ) : null}
            </section>
          }
          onAuthorised={(authorisationId) => {
            setAsking(false)
            create.mutate(authorisationId)
          }}
          onClose={() => {
            setAsking(false)
          }}
        />
      ) : null}
    </>
  )
}
